import { useState, useCallback, useRef, useEffect } from "react";
import {
  useLazyGenerateChatAttachmentSignedUrlQuery,
  useCreateChatAttachmentMutation,
  useDeleteChatAttachmentMutation,
  useLazyGeneratePendingAttachmentSignedUrlQuery,
  useCreatePendingAttachmentMutation,
  useAssociatePendingAttachmentsMutation,
  useLazyGetChatAttachmentQuery,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { trackAttachmentUploaded } from "@/analytics/posthog-events";
import { ChatAttachment, ChatAttachmentStatus } from "@/types/chat";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

// Allowed MIME types with their max sizes (in bytes)
const ALLOWED_MIME_TYPES: Record<string, { maxSize: number; label: string }> = {
  "application/pdf": { maxSize: 10 * 1024 * 1024, label: "PDF" },
  "image/png": { maxSize: 5 * 1024 * 1024, label: "PNG Image" },
  "image/jpeg": { maxSize: 5 * 1024 * 1024, label: "JPEG Image" },
  "image/webp": { maxSize: 5 * 1024 * 1024, label: "WebP Image" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    maxSize: 2 * 1024 * 1024,
    label: "Excel",
  },
  "text/csv": { maxSize: 1 * 1024 * 1024, label: "CSV" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    maxSize: 5 * 1024 * 1024,
    label: "Word Document",
  },
};

// Generate a unique session ID for pending attachments
const generateSessionId = () =>
  `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export interface PendingAttachment {
  id: string; // Temporary ID for UI
  file: File;
  progress: number;
  status: "uploading" | "uploaded" | "processing" | "ready" | "error";
  error?: string;
  attachment?: ChatAttachment; // Backend attachment once created
  isPending: boolean; // True if not yet associated with a chat
}

export interface UseChatAttachmentUploadResult {
  pendingAttachments: PendingAttachment[];
  /** Upload a file immediately (uses pending flow if no chatId) */
  uploadAttachment: (file: File, chatId?: string) => Promise<ChatAttachment | null>;
  /** Associate pending attachments with a chat */
  associateAttachmentsWithChat: (chatId: string) => Promise<ChatAttachment[]>;
  removeAttachment: (id: string, chatId?: string) => Promise<void>;
  clearAttachments: () => void;
  isUploading: boolean;
  hasPendingAttachments: boolean;
  getAttachmentIds: () => string[];
  getPendingAttachmentIds: () => string[];
  validateFile: (file: File) => { valid: boolean; error?: string };
  sessionId: string;
}

const useChatAttachmentUpload = (): UseChatAttachmentUploadResult => {
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [activeUploadCount, setActiveUploadCount] = useState(0);
  const [sessionId] = useState(() => generateSessionId());
  const pollingIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const isUploading = activeUploadCount > 0;
  const hasPendingAttachments = pendingAttachments.some(
    (p) => p.isPending && (p.status === "uploaded" || p.status === "processing" || p.status === "ready")
  );

  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const isAdmin = user?.userRole === roles.TDADMIN;
  const resolvedTenantIdForAdmin = selectedTenant?._id;

  const [getSignedUrl] = useLazyGenerateChatAttachmentSignedUrlQuery();
  const [getPendingSignedUrl] = useLazyGeneratePendingAttachmentSignedUrlQuery();
  const [createAttachment] = useCreateChatAttachmentMutation();
  const [createPendingAttachment] = useCreatePendingAttachmentMutation();
  const [associatePendingAttachments] = useAssociatePendingAttachmentsMutation();
  const [deleteAttachment] = useDeleteChatAttachmentMutation();
  const [getAttachment] = useLazyGetChatAttachmentQuery();
  const { show } = useSnackbar();

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervalsRef.current.forEach((interval) => clearInterval(interval));
      pollingIntervalsRef.current.clear();
    };
  }, []);

  // Start polling for attachment status
  const startPolling = useCallback(
    (attachmentId: string, pendingId: string) => {
      // Clear any existing polling for this attachment
      const existingInterval = pollingIntervalsRef.current.get(attachmentId);
      if (existingInterval) {
        clearInterval(existingInterval);
      }

      const interval = setInterval(async () => {
        try {
          const { data } = await getAttachment(attachmentId);
          if (data) {
            const newStatus =
              data.status === ChatAttachmentStatus.READY
                ? "ready"
                : data.status === ChatAttachmentStatus.FAILED
                ? "error"
                : "processing";

            setPendingAttachments((prev) =>
              prev.map((p) =>
                p.id === pendingId
                  ? {
                      ...p,
                      status: newStatus as PendingAttachment["status"],
                      attachment: data,
                      error: data.error?.message,
                    }
                  : p
              )
            );

            // Stop polling if status is terminal
            if (
              data.status === ChatAttachmentStatus.READY ||
              data.status === ChatAttachmentStatus.FAILED
            ) {
              clearInterval(interval);
              pollingIntervalsRef.current.delete(attachmentId);
            }
          }
        } catch (error) {
          console.error("Error polling attachment status:", error);
        }
      }, 1500); // Poll every 1.5 seconds

      pollingIntervalsRef.current.set(attachmentId, interval);
    },
    [getAttachment]
  );

  // Validate file type and size
  const validateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    const allowedType = ALLOWED_MIME_TYPES[file.type];

    if (!allowedType) {
      const supportedTypes = Object.values(ALLOWED_MIME_TYPES)
        .map((t) => t.label)
        .join(", ");
      return {
        valid: false,
        error: `Unsupported file type. Supported: ${supportedTypes}`,
      };
    }

    if (file.size > allowedType.maxSize) {
      const maxMB = allowedType.maxSize / (1024 * 1024);
      return {
        valid: false,
        error: `File too large. Max size for ${allowedType.label}: ${maxMB}MB`,
      };
    }

    return { valid: true };
  }, []);

  // Upload a file immediately (uses pending flow if no chatId provided)
  const uploadAttachment = useCallback(
    async (file: File, chatId?: string): Promise<ChatAttachment | null> => {
      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        show("Invalid file", validation.error || "Invalid file", "error");
        return null;
      }

      // TD_ADMIN must operate within a selected tenant
      if (isAdmin && !resolvedTenantIdForAdmin) {
        show(
          "Select a tenant",
          "As an admin, please select a tenant before uploading attachments.",
          "error"
        );
        return null;
      }

      // Create pending attachment for UI
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const pendingAttachment: PendingAttachment = {
        id: tempId,
        file,
        progress: 0,
        status: "uploading",
        isPending: !chatId, // If no chatId, this is a pending attachment
      };

      setPendingAttachments((prev) => [...prev, pendingAttachment]);
      setActiveUploadCount((n) => n + 1);

      try {
        let signedUrl: string;
        let key: string;

        if (chatId) {
          // Upload directly to chat
          const { data: signedUrlData } = await getSignedUrl({
            chatId,
            tenantId: isAdmin ? resolvedTenantIdForAdmin : undefined,
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          });

          if (!signedUrlData) {
            throw new Error("Failed to get upload URL");
          }

          signedUrl = signedUrlData.signedUrl;
          key = signedUrlData.key;
        } else {
          // Upload as pending attachment (no chat yet)
          const { data: pendingSignedUrlData } = await getPendingSignedUrl({
            sessionId,
            tenantId: isAdmin ? resolvedTenantIdForAdmin : undefined,
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          });

          if (!pendingSignedUrlData) {
            throw new Error("Failed to get upload URL");
          }

          signedUrl = pendingSignedUrlData.signedUrl;
          key = pendingSignedUrlData.key;
        }

        // Update progress
        setPendingAttachments((prev) =>
          prev.map((p) => (p.id === tempId ? { ...p, progress: 30 } : p))
        );

        // Upload file to S3
        const uploadResponse = await fetch(signedUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file to storage");
        }

        // Update progress
        setPendingAttachments((prev) =>
          prev.map((p) => (p.id === tempId ? { ...p, progress: 60 } : p))
        );

        // Create attachment record
        let attachment: ChatAttachment;

        if (chatId) {
          // Create regular attachment (triggers async ingestion)
          attachment = await createAttachment({
            chatId,
            tenantId: isAdmin ? resolvedTenantIdForAdmin : undefined,
            s3Key: key,
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          }).unwrap();

          // Start polling for ingestion status
          if (attachment._id) {
            startPolling(attachment._id, tempId);
          }
        } else {
          // Create pending attachment (no ingestion yet)
          attachment = await createPendingAttachment({
            sessionId,
            tenantId: isAdmin ? resolvedTenantIdForAdmin : undefined,
            s3Key: key,
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
          }).unwrap();
        }

        // Update pending attachment with backend data
        setPendingAttachments((prev) =>
          prev.map((p) =>
            p.id === tempId
              ? {
                  ...p,
                  progress: 100,
                  status: chatId ? "processing" : "uploaded", // Pending attachments are "uploaded" but not processing
                  attachment,
                  isPending: !chatId,
                }
              : p
          )
        );

        trackAttachmentUploaded(file.type);

        return attachment;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Upload failed";

        setPendingAttachments((prev) =>
          prev.map((p) =>
            p.id === tempId
              ? { ...p, status: "error" as const, error: errorMessage, progress: 0 }
              : p
          )
        );

        show("Upload failed", errorMessage, "error");
        return null;
      } finally {
        setActiveUploadCount((n) => Math.max(0, n - 1));
      }
    },
    [
      validateFile,
      show,
      isAdmin,
      resolvedTenantIdForAdmin,
      getSignedUrl,
      getPendingSignedUrl,
      createAttachment,
      createPendingAttachment,
      sessionId,
      startPolling,
    ]
  );

  // Associate pending attachments with a chat
  const associateAttachmentsWithChat = useCallback(
    async (chatId: string): Promise<ChatAttachment[]> => {
      const pendingIds = pendingAttachments
        .filter((p) => p.isPending && p.attachment?._id)
        .map((p) => p.attachment!._id);

      if (pendingIds.length === 0) {
        return [];
      }

      try {
        const attachments = await associatePendingAttachments({
          chatId,
          attachmentIds: pendingIds,
        }).unwrap();

        // Update local state: mark as no longer pending and start polling
        setPendingAttachments((prev) =>
          prev.map((p) => {
            if (p.isPending && p.attachment?._id && pendingIds.includes(p.attachment._id)) {
              // Start polling for this attachment
              startPolling(p.attachment._id, p.id);
              return {
                ...p,
                isPending: false,
                status: "processing" as const,
              };
            }
            return p;
          })
        );

        return attachments;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to associate attachments";
        show("Error", errorMessage, "error");
        return [];
      }
    },
    [pendingAttachments, associatePendingAttachments, show, startPolling]
  );

  // Remove an attachment
  const removeAttachment = useCallback(
    async (id: string, chatId?: string) => {
      const attachment = pendingAttachments.find((p) => p.id === id);

      // Clear any polling for this attachment
      if (attachment?.attachment?._id) {
        const interval = pollingIntervalsRef.current.get(attachment.attachment._id);
        if (interval) {
          clearInterval(interval);
          pollingIntervalsRef.current.delete(attachment.attachment._id);
        }

        // Delete from backend
        try {
          await deleteAttachment({
            id: attachment.attachment._id,
            chatId,
          }).unwrap();
        } catch (error) {
          console.error("Failed to delete attachment from backend:", error);
        }
      }

      setPendingAttachments((prev) => prev.filter((p) => p.id !== id));
    },
    [pendingAttachments, deleteAttachment]
  );

  // Clear all attachments
  const clearAttachments = useCallback(() => {
    // Clear all polling intervals
    pollingIntervalsRef.current.forEach((interval) => clearInterval(interval));
    pollingIntervalsRef.current.clear();
    setPendingAttachments([]);
  }, []);

  // Get IDs of attachments that are ready or processing (for sending with message)
  const getAttachmentIds = useCallback((): string[] => {
    return pendingAttachments
      .filter(
        (p) =>
          p.attachment?._id &&
          !p.isPending &&
          (p.status === "ready" || p.status === "processing" || p.status === "uploaded")
      )
      .map((p) => p.attachment!._id);
  }, [pendingAttachments]);

  // Get IDs of pending attachments (not yet associated with a chat)
  const getPendingAttachmentIds = useCallback((): string[] => {
    return pendingAttachments
      .filter((p) => p.isPending && p.attachment?._id)
      .map((p) => p.attachment!._id);
  }, [pendingAttachments]);

  return {
    pendingAttachments,
    uploadAttachment,
    associateAttachmentsWithChat,
    removeAttachment,
    clearAttachments,
    isUploading,
    hasPendingAttachments,
    getAttachmentIds,
    getPendingAttachmentIds,
    validateFile,
    sessionId,
  };
};

export default useChatAttachmentUpload;
